import React, { useState, useEffect } from "react";
import axios from "axios";

export default function InviteAccept({ API_BASE, onLoginSuccess }) {
  const query = new URLSearchParams(window.location.search);
  const tenant = query.get("tenant") || "default";
  const token = query.get("token") || "";

  // Parse expiration timestamp
  const rawTimestamp = token.replace("INV-", "");
  const expireTime = parseInt(rawTimestamp, 10);
  const isInvalidToken = isNaN(expireTime);

  const [timeLeft, setTimeLeft] = useState(() => {
    if (isInvalidToken) return 0;
    return Math.max(0, Math.floor((expireTime - Date.now()) / 1000));
  });

  const isExpired = !isInvalidToken && timeLeft <= 0;

  useEffect(() => {
    if (isInvalidToken || expireTime <= Date.now()) return;
    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.floor((expireTime - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [expireTime, isInvalidToken]);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const formatTime = (secs) => {
    const mins = Math.floor(secs / 60);
    const remainder = secs % 60;
    return `${mins}m ${remainder < 10 ? '0' : ''}${remainder}s`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (isExpired) {
      setError("Invitation link has expired. Please ask your administrator for a new link.");
      return;
    }

    if (!name.trim() || !email.trim() || !password.trim()) {
      setError("Please fill in all required fields.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/users`,
        {
          name: name.trim(),
          email: email.trim().toLowerCase(),
          password: password.trim(),
          role: "QA Specialist"
        },
        {
          headers: {
            "x-tenant-id": tenant
          }
        }
      );

      if (res.data.error) {
        setError(res.data.error);
        setLoading(false);
        return;
      }

      setSuccess(true);
      setLoading(false);

      localStorage.setItem("rabbit_tenant", tenant);
      localStorage.setItem("rabbit_tenant_id", tenant);

      setTimeout(() => {
        if (onLoginSuccess) {
          onLoginSuccess({
            name: name.trim(),
            email: email.trim().toLowerCase(),
            role: "QA Specialist"
          });
        }
        window.location.href = "/";
      }, 1500);

    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.error || err.message || "Failed to set up account.");
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#002247", display: "flex", justifyContent: "center", alignItems: "center", padding: "20px" }}>
      <div style={{ background: "#ffffff", borderRadius: "20px", padding: "40px", width: "100%", maxWidth: "460px", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.3)", textAlign: "left" }}>
        
        {/* Logo / Header */}
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <img src="/qintra_logo.png" alt="Qintra" style={{ height: "46px", objectFit: "contain", marginBottom: "12px" }} />
          <h2 style={{ margin: 0, fontSize: "24px", fontWeight: "800", color: "#0f172a" }}>
            Join Team Workspace
          </h2>
          <p style={{ margin: "6px 0 0 0", fontSize: "13px", color: "#64748b" }}>
            You've been invited to join <strong style={{ color: "#FD6101" }}>{tenant.toUpperCase()}</strong> workspace
          </p>

          {!isExpired && !success && (
            <div style={{ marginTop: "12px", display: "inline-block", background: "#fff7ed", color: "#FD6101", fontSize: "12px", fontWeight: "800", padding: "6px 14px", borderRadius: "20px" }}>
              ⏳ Link expires in: {formatTime(timeLeft)}
            </div>
          )}
        </div>

        {isExpired ? (
          <div style={{ padding: "24px", borderRadius: "14px", background: "#fef2f2", border: "1px solid #fca5a5", color: "#991b1b", textAlign: "center" }}>
            <div style={{ fontSize: "36px", marginBottom: "10px" }}>⏰</div>
            <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "800" }}>Invitation Link Expired</h3>
            <p style={{ margin: "8px 0 0 0", fontSize: "13px", color: "#7f1d1d", lineHeight: "1.5" }}>
              This security invitation link was valid for <strong>3 minutes</strong> and has expired.
            </p>
            <p style={{ margin: "12px 0 0 0", fontSize: "12px", color: "#991b1b", fontWeight: "600" }}>
              Please ask your Workspace Administrator to generate a new invite link.
            </p>
          </div>
        ) : error ? (
          <div style={{ padding: "12px 14px", borderRadius: "8px", background: "#fef2f2", border: "1px solid #fca5a5", color: "#b91c1c", fontSize: "13px", fontWeight: "600", marginBottom: "20px" }}>
            ⚠️ {error}
          </div>
        ) : null}

        {!isExpired && (
          success ? (
            <div style={{ padding: "20px", borderRadius: "12px", background: "#ecfdf5", border: "1px solid #6ee7b7", color: "#047857", textAlign: "center" }}>
              <div style={{ fontSize: "32px", marginBottom: "8px" }}>🎉</div>
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "800" }}>Account Created Successfully!</h3>
              <p style={{ margin: "6px 0 0 0", fontSize: "13px" }}>Redirecting you to your workspace dashboard...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#334155", marginBottom: "6px" }}>
                  Full Name
                </label>
                <input
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  style={{ width: "100%", padding: "12px 14px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "14px", outline: "none" }}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#334155", marginBottom: "6px" }}>
                  Work Email Address (Username)
                </label>
                <input
                  type="email"
                  placeholder="john@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={{ width: "100%", padding: "12px 14px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "14px", outline: "none" }}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#334155", marginBottom: "6px" }}>
                  Create Password
                </label>
                <input
                  type="password"
                  placeholder="••••••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{ width: "100%", padding: "12px 14px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "14px", outline: "none" }}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#334155", marginBottom: "6px" }}>
                  Confirm Password
                </label>
                <input
                  type="password"
                  placeholder="••••••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  style={{ width: "100%", padding: "12px 14px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "14px", outline: "none" }}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  marginTop: "10px",
                  padding: "14px",
                  borderRadius: "10px",
                  border: "none",
                  background: "#FD6101",
                  color: "#ffffff",
                  fontSize: "15px",
                  fontWeight: "800",
                  cursor: loading ? "not-allowed" : "pointer",
                  boxShadow: "0 4px 12px rgba(253, 97, 1, 0.3)"
                }}
                onMouseEnter={e => { if (!loading) e.currentTarget.style.background = "#e05300"; }}
                onMouseLeave={e => { if (!loading) e.currentTarget.style.background = "#FD6101"; }}
              >
                {loading ? "Creating Account..." : "🚀 Join Workspace"}
              </button>
            </form>
          )
        )}
      </div>
    </div>
  );
}

